import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import { PositionUpdateSourceEnum, ProductTypeEnum, type PersistedDefaultTabProps } from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import {
  BLOTTER_ID_POSITIONS_SUBACCOUNT,
  BLOTTER_ID_SPOT_POSITIONS,
  BLOTTER_ID_UNIFIED_DERIVATIVES,
  BLOTTER_ID_UNIFIED_SPOT_AND_DERIVS,
} from './tokens';
import type {
  DerivativesPositionsTabProps,
  PositionColumn,
  PositionsTabProps,
  PositionsTabType,
  PositionsTableFilter,
  SpotPositionsTabProps,
  SubAccountPositionsTabProps,
  UnifiedPositionColumn,
  UnifiedPositionsTabProps,
} from './types';

export const defaultSpotBlotterColumns = [
  'reconWarning',
  { field: 'Asset', initialRowGroup: true, initialHide: true },
  'MarketAccount',
  'Amount',
  'AvgCost',
  'TotalPnL',
] satisfies PositionColumn[];

export const defaultSpotBlotterFilter: PositionsTableFilter = {
  AssetTypes: [ProductTypeEnum.Spot],
  Source: PositionUpdateSourceEnum.Internal,
};

export const defaultSpotBlotterTab: Omit<SpotPositionsTabProps, 'id' | 'label'> = {
  defaultColumns: defaultSpotBlotterColumns,
  defaultFilter: defaultSpotBlotterFilter,
  tabType: 'Spot',
  showZeroBalances: false,
};

export const defaultDerivativesBlotterColumns = [
  'reconWarning',
  { id: 'underlying', initialRowGroup: true, initialHide: true },
  { id: 'instrument' },
  'MarketAccount',
  'Amount',
  'AvgCost',
  'MarkPrice',
  'UnderlyingPrice',
  'LiquidationPrice',
  'elpVsMp',
  'Delta',
  'Equivalent.Delta',
  'UnrealizedPnL',
  { field: 'AssetType', initialRowGroup: true, initialHide: true },
] satisfies PositionColumn[];

export const defaultDerivativesBlotterFilter: PositionsTableFilter = {
  Source: PositionUpdateSourceEnum.Gateway,
};

export const defaultDerivativesBlotterTab: Omit<DerivativesPositionsTabProps, 'id' | 'label'> = {
  defaultColumns: defaultDerivativesBlotterColumns,
  defaultFilter: defaultDerivativesBlotterFilter,
  tabType: 'Derivatives',
  showZeroBalances: false,
};

export const defaultUnifiedBlotterColumns = [
  'reconWarning',
  { id: 'MarketAccount', initialRowGroup: true, initialRowGroupIndex: 0, initialHide: true },
  { id: 'underlying', initialRowGroup: true, initialRowGroupIndex: 1, initialHide: true },
  { id: 'Asset', initialRowGroup: true, initialRowGroupIndex: 2, initialHide: true },
  'Amount',
  'Equivalent.Amount',
  'Equivalent.Delta',
  'AvgCost',
  'MarkPrice',
  'UnderlyingPrice',
  'Equivalent.UnrealizedPnL',
  'venueEquityEquivalent',
  'talosEquityEquivalent',
  'Equivalent.initialMargin',
  'Equivalent.maintenanceMargin',
  'LiquidationPrice',
  'elpVsMp',
] satisfies UnifiedPositionColumn[];

export const defaultUnifiedBlotterFilter: PositionsTableFilter = {
  Source: PositionUpdateSourceEnum.Gateway,
};

export const defaultUnifiedBlotterTab: Omit<UnifiedPositionsTabProps, 'id' | 'label'> = {
  defaultColumns: defaultUnifiedBlotterColumns,
  defaultFilter: defaultUnifiedBlotterFilter,
  tabType: 'Unified',
  showZeroBalances: false,
};

export const defaultSubAccountsBlotterColumns = [
  { field: 'SubAccount', id: 'SubAccount', initialRowGroup: true, initialHide: true, initialSort: '+' },
  'Asset',
  'Amount',
  'Equivalent.Amount',
  'AvgCost',
  'MarkPrice',
  'Delta',
  'Equivalent.Delta',
  'Equivalent.CumFees',
  'Equivalent.UnrealizedPnL',
  'Equivalent.RealizedPnL',
  'Equivalent.FundingPnL',
  'Equivalent.TotalPnL',
] satisfies PositionColumn[];

export const defaultSubAccountsBlotterFilter: PositionsTableFilter = {};

export const defaultSubAccountsBlotterTab: Omit<SubAccountPositionsTabProps, 'id' | 'label'> = {
  defaultColumns: defaultSubAccountsBlotterColumns,
  defaultFilter: defaultSubAccountsBlotterFilter,
  tabType: 'Sub Accounts',
  showZeroBalances: false,
};

export const POSITIONS_TABS_EXCLUDED_IDS = [
  BLOTTER_ID_POSITIONS_SUBACCOUNT,
  BLOTTER_ID_UNIFIED_DERIVATIVES,
  BLOTTER_ID_SPOT_POSITIONS,
  BLOTTER_ID_UNIFIED_SPOT_AND_DERIVS,
];

export function useEnabledPositionsTabTypes(): PositionsTabType[] {
  const { enableSpotPositionsBlotter, enableDerivativesPositionsBlotter, enableUnifiedPositionsBlotter } =
    useFeatureFlag();
  const { allSubAccounts } = useSubAccounts();
  const enableSubAccountsPositionsBlotter = allSubAccounts != null && allSubAccounts.length > 0;

  return useMemo(
    () =>
      compact<PositionsTabType>([
        enableSpotPositionsBlotter ? 'Spot' : undefined,
        enableDerivativesPositionsBlotter ? 'Derivatives' : undefined,
        enableUnifiedPositionsBlotter ? 'Unified' : undefined,
        enableSubAccountsPositionsBlotter ? 'Sub Accounts' : undefined,
      ]),
    [
      enableSpotPositionsBlotter,
      enableDerivativesPositionsBlotter,
      enableUnifiedPositionsBlotter,
      enableSubAccountsPositionsBlotter,
    ]
  );
}

export function useDefaultPositionsTabs(): PersistedDefaultTabProps<PositionsTabProps>[] {
  const { enableSpotPositionsBlotter, enableDerivativesPositionsBlotter, enableUnifiedPositionsBlotter } =
    useFeatureFlag();
  const { allSubAccounts } = useSubAccounts();
  const enableSubAccountsPositionsBlotter = allSubAccounts != null && allSubAccounts.length > 0;

  return useMemo(() => {
    const tabs: PersistedDefaultTabProps<PositionsTabProps>[] = [];

    if (enableSpotPositionsBlotter) {
      tabs.push({
        label: 'Spot',
        id: BLOTTER_ID_SPOT_POSITIONS,
        ...defaultSpotBlotterTab,
      });
    }

    if (enableDerivativesPositionsBlotter) {
      tabs.push({
        label: 'Derivatives',
        id: BLOTTER_ID_UNIFIED_DERIVATIVES,
        ...defaultDerivativesBlotterTab,
      });
    }

    if (enableUnifiedPositionsBlotter) {
      tabs.push({
        label: 'Unified',
        id: BLOTTER_ID_UNIFIED_SPOT_AND_DERIVS,
        ...defaultUnifiedBlotterTab,
      });
    }

    if (enableSubAccountsPositionsBlotter) {
      tabs.push({
        label: 'Sub Accounts',
        id: BLOTTER_ID_POSITIONS_SUBACCOUNT,
        ...defaultSubAccountsBlotterTab,
      });
    }

    return tabs;
  }, [
    enableSpotPositionsBlotter,
    enableDerivativesPositionsBlotter,
    enableUnifiedPositionsBlotter,
    enableSubAccountsPositionsBlotter,
  ]);
}
